import * as React from 'react'
import { ReactElement, useEffect, useState } from 'react'
import BaseCombobox, { BaseComboboxProps } from '../BaseCombobox'
import { I18nNomenclature } from '../../../config/i18nConfig'
import { useTranslation } from 'react-i18next'
import { parseDate } from '../../../helpers/dateHelper'
import { DateTime } from 'luxon'

export type I18NComboboxProps<T extends I18nNomenclature> = //
    Omit<BaseComboboxProps<T, I18NOption>, 'getEntityValue' | 'getEntityLabel'> & {
        idKey: keyof T
    }

export type I18NOption = I18nNomenclature & {
    children?: I18NOption[]
}

export const isActive = <T extends I18nNomenclature>(entity: T): boolean => {
    return (
        (parseDate(entity.validFrom) || DateTime.local(1970, 1, 1)) <= DateTime.now().startOf('day') &&
        (parseDate(entity.validTo) || DateTime.local(3000, 1, 1)) >= DateTime.now().startOf('day')
    )
}

export const defaultFilter = <T extends I18nNomenclature>(props: I18NComboboxProps<T>, entity: T): boolean => {
    return props.filter?.(entity) ?? (isActive(entity) || props.selectedOptions?.includes(entity[props.idKey]?.toString()))
}

export const getI18NName = <T extends I18nNomenclature>(nomenclature: T, language: string): string => {
    let find = nomenclature?.names?.find(name => name.language.toLowerCase() === language.toLowerCase())
    if (find === undefined) {
        nomenclature?.names?.find(name => name.language.toLowerCase() === 'nl')
    }
    if (find === undefined) {
        find = nomenclature?.names?.[0]
    }
    return find?.value ?? '?'
}

export default function I18NCombobox<T extends I18nNomenclature>(props: I18NComboboxProps<T>): ReactElement {
    const { i18n: i18n, t: translate } = useTranslation()
    const [sortedEntities, setSortedEntities] = useState<I18NOption[]>([])

    useEffect(() => {
        const positionI18Ns: I18NOption = {
            names: [{ language: i18n.language, value: translate('base.i18ncombobox.position') }],
            children: (props.entities || [])
                .filter((entity: T) => entity.position)
                .filter((entity: T) => defaultFilter(props, entity))
                .sort((a: T, b: T) => a.position - b.position),
        }
        const miscI18Ns: I18NOption = {
            names: [{ language: i18n.language, value: translate('base.other') }],
            children: (props.entities || [])
                .filter((I18N: T) => !I18N.position)
                .filter((entity: T) => defaultFilter(props, entity))
                .sort((a: T, b: T) => getI18NName(a, i18n.language).localeCompare(getI18NName(b, i18n.language))),
        }

        if (positionI18Ns.children.length > 0 && miscI18Ns.children.length > 0) {
            setSortedEntities([positionI18Ns, miscI18Ns])
        } else if (positionI18Ns.children.length > 0) {
            setSortedEntities(positionI18Ns.children)
        } else if (miscI18Ns.children.length > 0) {
            setSortedEntities(miscI18Ns.children)
        } else {
            setSortedEntities([])
        }
    }, [i18n.language, props.entities])

    return (
        <BaseCombobox<I18NOption>
            sort={(a: T, b: T) => (a.position ?? 100) - (b.position ?? 100)}
            isGroup={entity => entity.children?.length > 0}
            getGroupEntities={entity => entity.children}
            getGroupLabel={entity => entity.names[0].value}
            getGroupValue={entity => getI18NName(entity as T, i18n.language)}
            getEntityValue={entity => (entity as T)[props.idKey]?.toString()}
            getEntityLabel={entity => getI18NName(entity as T, i18n.language)}
            {...props}
            entities={sortedEntities}
        />
    )
}
